import Typography from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { ReactNode } from 'react';

const aspects = {
  square: 'aspect-square',
  video: 'aspect-video',
  portrait: 'aspect-4/5',
  landscape: 'aspect-4/3',
} as const;

/**
 * Layout hints only — the browser uses these to reserve the right aspect
 * ratio before the image loads, so an approximation matching the ratio is
 * enough; it does not need to equal the served file's actual dimensions.
 */
const dimensions: Partial<Record<keyof typeof aspects, [number, number]>> = {
  video: [1000, 562],
  square: [800, 800],
};

interface EntityCardProps {
  href: string;
  image: string;
  alt: string;
  aspect?: keyof typeof aspects;
  /**
   * `compact` is the product grid: borderless, image-led, text beneath.
   * `default` is the bordered card used for courses and services.
   */
  density?: 'default' | 'compact';
  eyebrow?: ReactNode;
  title: string;
  description?: string;
  /** Price rows, swatches — anything that sits under the title. */
  footer?: ReactNode;
  /** Pinned to the top-left of the image. A discount flash, a stock notice. */
  imageBadge?: ReactNode;
  className?: string;
}

/**
 * The one card shell for courses, products and services.
 *
 * These were three forked components with three different hover treatments for
 * the same shape — a muted tint, an orange glow, and a plain shadow — plus a
 * fourth copy inlined on the home page. The hover state here is the site's seam
 * motif: the card lifts and a running stitch is pinned around it.
 */
const EntityCard = ({
  href,
  image,
  alt,
  aspect = 'landscape',
  density = 'default',
  eyebrow,
  title,
  description,
  footer,
  imageBadge,
  className,
}: EntityCardProps) => {
  const compact = density === 'compact';
  const [width, height] = dimensions[aspect] ?? [];

  return (
    <Link href={href} className={cn('group flex h-full', className)}>
      {/*
        One shell for every card on the site — products included. rounded-xl,
        not the rounded-lg this used to carry: every page-level card (home,
        about, career) is rounded-xl, and the odd radius here was what made the
        grids look like they came from a different kit.
      */}
      <article className="relative flex h-full w-full flex-col overflow-hidden rounded-xl border bg-card shadow-card transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:shadow-pop">
        <div
          className={cn('relative overflow-hidden bg-muted', aspects[aspect])}
        >
          <img
            className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            src={image}
            alt={alt}
            width={width}
            height={height}
            loading="lazy"
          />

          {imageBadge && (
            <div className="absolute left-2 top-2">{imageBadge}</div>
          )}
        </div>

        <div
          className={cn(
            'flex flex-1 flex-col items-start',
            compact ? 'gap-1.5 p-3' : 'gap-2 p-4'
          )}
        >
          {/*
            The description runs to five lines; the title is held to two.

            The default density's title was previously unclamped, so one long
            course name pushed its card taller than every sibling in the row and
            the grid lost its baseline. Only ProductCard omits a description, so
            the five-line cap reaches course and service cards alone.
          */}
          {eyebrow}
          {compact ? (
            <Typography.Body className="line-clamp-2 font-semibold capitalize transition-colors duration-300 group-hover:text-primary">
              {title}
            </Typography.Body>
          ) : (
            // h3, not the Large style's default h4: every page that renders
            // this density sits the grid directly under an h2 (or, on a
            // couple of pages, straight under the Banner's h1 with no h2/h3
            // section heading at all) — h4 here skipped a level either way.
            <Typography.Large
              as="h3"
              className="line-clamp-2 font-bold capitalize"
            >
              {title}
            </Typography.Large>
          )}
          {description && (
            <Typography.Subtle className="line-clamp-5">
              {description}
            </Typography.Subtle>
          )}
          {/*
            mt-auto only on the bordered card, where pinning the CTA to the
            bottom edge is the point. On a product it pushed the price to the
            floor of whichever card in the row happened to be tallest, opening
            a hole between the name and the price on every shorter one.
          */}
          {footer && (
            <div className={cn('w-full', compact ? 'pt-0.5' : 'mt-auto pt-3')}>
              {footer}
            </div>
          )}
        </div>

        {/* The seam, drawn inside the card's own bounds so it reads as a
            stitched edge rather than a second border. */}
        <div
          aria-hidden
          className="seam-outline pointer-events-none absolute inset-1.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        />
      </article>
    </Link>
  );
};

export default EntityCard;
